import { useEffect, useRef, useState } from "react";

interface UseBackToTopVisibilityOptions {
  /**
   * Duration of the fade-out animation in milliseconds
   * @default 250
   */
  animationDuration?: number;
  /**
   * Intersection observer threshold
   * @default 0
   */
  threshold?: number;
}

/**
 * Custom hook to manage back-to-top button visibility based on a sentinel element
 *
 * @param sentinelRef - Ref to the element that triggers visibility changes (typically near submit button)
 * @param options - Configuration options for animation and intersection observer
 * @returns Object containing visibility state and animation state
 */
export function useBackToTopVisibility(
  sentinelRef: React.RefObject<HTMLElement>,
  options: UseBackToTopVisibilityOptions = {}
) {
  const { animationDuration = 250, threshold = 0 } = options;

  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const target = sentinelRef.current;
    if (!target) return;

    const clearHideTimeout = () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldShow = !entry.isIntersecting;

        if (shouldShow && !showBackToTop) {
          clearHideTimeout();
          setShowBackToTop(true);
          setIsAnimatingOut(false);
        } else if (!shouldShow && showBackToTop) {
          setIsAnimatingOut(true);
          clearHideTimeout();
          hideTimeoutRef.current = setTimeout(() => {
            setShowBackToTop(false);
            setIsAnimatingOut(false);
            hideTimeoutRef.current = null;
          }, animationDuration);
        }
      },
      { threshold }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
      clearHideTimeout();
    };
  }, [showBackToTop, sentinelRef, animationDuration, threshold]);

  return {
    showBackToTop,
    isAnimatingOut,
  };
}
